import type { ExternalBridge } from '@hibro/platform-renderer';
import type { ExternalStatus } from '@hibro/shared';

/** Snapshot returned when the preload bridge is absent (e.g. web build). */
const OFFLINE: ExternalStatus = { running: false };

/**
 * Build the renderer-side `ExternalBridge` over the preload `window.hibroExternal`
 * IPC bridge (架构方案 §3.1/§5.8). Lives in the app shell so the platform
 * package stays free of the preload bridge; methods degrade to no-ops when the
 * bridge is unavailable (mirrors `createIframeBridge`). Every call is keyed by
 * process key (`<moduleId>` or `<moduleId>#<entryId>`), which the main process
 * resolves back to the module + entry.
 */
export function createExternalBridge(): ExternalBridge {
  return {
    launch: (processKey) =>
      window.hibroExternal?.launch(processKey) ?? Promise.resolve(OFFLINE),
    activate: (processKey) =>
      window.hibroExternal?.activate(processKey) ?? Promise.resolve(),
    close: (processKey) =>
      window.hibroExternal?.close(processKey) ?? Promise.resolve(),
    status: (processKey) =>
      window.hibroExternal?.status(processKey) ?? Promise.resolve(OFFLINE),
    // Process-table changes ride the unified surface enumeration (§5.8): the main
    // process re-broadcasts it whenever an external process starts or exits, so a
    // module panel can refresh its run state without polling.
    onDidChange: (listener) =>
      window.hibroSurfaces?.onChange(() => listener()) ?? (() => undefined),
  };
}
